import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDark, useStorage } from '@vueuse/core'

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'
export type NavigationMode = 'classic' | 'sidebar-v2'

export const useUiStore = defineStore('ui', () => {
  // Theme persistence via @vueuse/core useStorage (localStorage + serialization).
  const themeMode = useStorage<ThemeMode>('espelhacontas.theme', 'dark', undefined, {
    serializer: {
      read: (v) => (v === 'light' || v === 'dark' || v === 'system' ? v : 'dark'),
      write: (v) => v,
    },
  })

  // useDark handles the system preference + reactive application of `.dark` on html.
  const isDark = useDark({
    selector: 'html',
    attribute: 'class',
    valueDark: 'dark',
    valueLight: '',
    storageKey: 'espelhacontas.theme-resolved',
    initialValue: 'dark',
  })

  const sidebarCollapsed = useStorage<boolean>('espelhacontas.sidebar-collapsed', false)
  const navigationMode = useStorage<NavigationMode>('espelhacontas.navigation-mode', 'classic')

  const realtimeState = ref<'idle' | 'connecting' | 'open' | 'closed'>('idle')
  const realtimeError = ref<string | null>(null)

  const resolvedTheme = computed<ResolvedTheme>(() => (isDark.value ? 'dark' : 'light'))

  function setTheme(mode: ThemeMode) {
    themeMode.value = mode
    if (mode === 'system') {
      isDark.value = window.matchMedia('(prefers-color-scheme: dark)').matches
    } else {
      isDark.value = mode === 'dark'
    }
  }

  function toggleTheme() {
    setTheme(resolvedTheme.value === 'dark' ? 'light' : 'dark')
  }

  function setNavigationMode(mode: NavigationMode) { navigationMode.value = mode }

  function toggleSidebar() {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  function setRealtimeState(state: 'idle' | 'connecting' | 'open' | 'closed', error: string | null = null) {
    realtimeState.value = state
    realtimeError.value = error
  }

  return {
    themeMode,
    isDark,
    resolvedTheme,
    sidebarCollapsed,
    navigationMode,
    realtimeState,
    realtimeError,
    setTheme,
    toggleTheme,
    toggleSidebar,
    setNavigationMode,
    setRealtimeState,
  }
})
